import { useState } from 'react'

interface FeatureToggleProps {
  featureKey: string
  name: string
  description: string
  hasAccess: boolean
  source: 'override' | 'role' | 'default' | 'none'
  onToggle: (featureKey: string, enabled: boolean) => Promise<void>
  disabled?: boolean
}

export default function FeatureToggle({
  featureKey,
  name,
  description,
  hasAccess,
  source,
  onToggle,
  disabled = false,
}: FeatureToggleProps) {
  const [isLoading, setIsLoading] = useState(false)

  const handleToggle = async () => {
    if (disabled || isLoading) return

    // Can only toggle overrides - role and default features can't be toggled
    if (source === 'role' || source === 'default') return

    setIsLoading(true)
    try {
      await onToggle(featureKey, !hasAccess)
    } finally {
      setIsLoading(false)
    }
  }

  const isLocked = source === 'role' || source === 'default'

  // Source badge text and styling
  const sourceStyles: Record<string, { bg: string; text: string; label: string }> = {
    override: { bg: 'bg-teal-500/20', text: 'text-teal-400', label: 'Override' },
    role: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Role' },
    default: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Default' },
    none: { bg: 'bg-transparent', text: 'text-gray-500', label: '' },
  }

  const sourceStyle = sourceStyles[source] || sourceStyles.none

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
        hasAccess ? 'bg-[#1c2630]' : 'bg-[#141c24]'
      } ${!isLocked && !disabled ? 'hover:bg-[#1c2630] cursor-pointer' : ''} ${
        isLoading ? 'opacity-50' : ''
      }`}
      onClick={handleToggle}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#e8eaed]">{name}</span>
          {source !== 'none' && hasAccess && (
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sourceStyle.bg} ${sourceStyle.text}`}
            >
              {sourceStyle.label}
            </span>
          )}
          {isLocked && (
            <svg
              className="w-3.5 h-3.5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          )}
        </div>
        <p className="text-xs text-[#8b949e] mt-0.5 truncate">{description}</p>
      </div>

      {/* Toggle switch */}
      <div className="ml-4 flex-shrink-0">
        <button
          type="button"
          role="switch"
          aria-checked={hasAccess}
          disabled={isLocked || disabled || isLoading}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-[#0a0f14] ${
            hasAccess ? 'bg-teal-500' : 'bg-gray-600'
          } ${isLocked || disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            handleToggle()
          }}
        >
          <span className="sr-only">Toggle {name}</span>
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              hasAccess ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
    </div>
  )
}

// Feature category grouping component
interface FeatureGroupProps {
  title: string
  children: React.ReactNode
}

export function FeatureGroup({ title, children }: FeatureGroupProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-[#8b949e] px-1">
        {title}
      </h4>
      <div className="space-y-1">{children}</div>
    </div>
  )
}
